// User-spezifischer LocalStorage-Wrapper
//
// Hängt transparent den aktuellen User-Suffix (`::uid::<userId>`) an alle Keys,
// die als "user-spezifisch" klassifiziert sind. Das verhindert Datenverlust
// beim User-Wechsel auf demselben Gerät — stattdessen hat jeder User seinen
// eigenen Namespace.
//
// Verwendung:
//   import { userStorage } from './utils/userStorage';
//   userStorage.setItem('zielplanung_daily_todos', JSON.stringify(data));
//   const raw = userStorage.getItem('zielplanung_daily_todos');
//
// API ist identisch zu localStorage, deshalb kann einfach `localStorage`
// durch `userStorage` ersetzt werden.

const LAST_USER_KEY = 'last_logged_user_id';

// Keys, die user-spezifisch sein sollen (Prefix-Matching per startsWith).
// Alle anderen Keys bleiben global (z.B. token, fcmToken, biometric_*, PWA-Flags).
const USER_SPECIFIC_PREFIXES = [
  'zielplanung_',
  'burningteam_contact_',
  'burningteam_hide_calendar_tip',
  'burningteam_pending_calendar_transfer',
  'burningteam_wizard_goals',
  'burningteam_warteliste',
  'burningteam_schicht_rhythmus',
  'pendingNotificationOpen',
  'pendingSchuldFromInvoice',
  'customVeranstaltungOptionen',
  'schuldenPdfTipSeen',
  'rechnungen_markup_memory',
];

// Prüft ob ein Key user-spezifisch ist
const isUserSpecific = (key) => {
  if (!key) return false;
  return USER_SPECIFIC_PREFIXES.some((p) => key === p || key.startsWith(p));
};

// Aktuell eingeloggter User (wird von Login.js gesetzt)
let currentUserId = null;

const suffixedKey = (key) => {
  if (!isUserSpecific(key) || !currentUserId) return key;
  return `${key}::uid::${currentUserId}`;
};

// Migration: Wenn für den aktuellen User noch keine Migration gemacht wurde,
// kopiere alle existierenden globalen Keys (ohne Suffix) in den User-Namespace.
// Wird genau einmal pro User auf diesem Gerät ausgeführt.
const runMigrationForUser = (userId) => {
  if (!userId) return;
  const migrationFlag = `__userstorage_migrated__::${userId}`;
  if (localStorage.getItem(migrationFlag)) return;

  const keysToMigrate = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k) continue;
    if (k.startsWith('__userstorage_migrated__')) continue;
    if (!isUserSpecific(k)) continue;
    if (k.includes('::uid::')) continue;  // bereits prefixed
    keysToMigrate.push(k);
  }

  keysToMigrate.forEach((k) => {
    const newKey = `${k}::uid::${userId}`;
    if (localStorage.getItem(newKey) === null) {
      // Nur migrieren, wenn Ziel-Key noch leer ist — sonst User-Daten überschreiben!
      const v = localStorage.getItem(k);
      if (v !== null) localStorage.setItem(newKey, v);
    }
    // Alte globale Version entfernen, damit kein Leak zwischen Usern entsteht
    localStorage.removeItem(k);
  });

  localStorage.setItem(migrationFlag, new Date().toISOString());
};

export const userStorage = {
  // Aktuellen User setzen (von Login.js aufrufen nach erfolgreicher Auth)
  setCurrentUserId(userId) {
    currentUserId = userId ? String(userId) : null;
    if (currentUserId) {
      localStorage.setItem(LAST_USER_KEY, currentUserId);
      runMigrationForUser(currentUserId);
    }
  },

  // Aktuellen User zurücksetzen (von auth.js beim Logout aufrufen)
  clearCurrentUserId() {
    currentUserId = null;
  },

  getCurrentUserId() {
    return currentUserId;
  },

  // Beim App-Start wiederherstellen (falls noch kein neuer Login stattgefunden hat)
  init() {
    const saved = localStorage.getItem(LAST_USER_KEY);
    if (saved) currentUserId = saved;
  },

  // LocalStorage-kompatible API
  getItem(key) {
    return localStorage.getItem(suffixedKey(key));
  },
  setItem(key, value) {
    return localStorage.setItem(suffixedKey(key), value);
  },
  removeItem(key) {
    return localStorage.removeItem(suffixedKey(key));
  },
  key(index) {
    return localStorage.key(index);
  },
  get length() {
    return localStorage.length;
  },
  clear() {
    // Nicht delegieren an localStorage.clear() — zu gefährlich.
    // Stattdessen nur die eigenen user-specific keys des aktuellen Users entfernen.
    if (!currentUserId) return;
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.includes(`::uid::${currentUserId}`)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  },
};

// Initialisierung beim Import
userStorage.init();
